import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $userAccountsStore } from '@lib/store';
import { cn, PermissionUtils } from '@shared/utils';
import type { UserResponse } from '@shared/api';
import { RemoveAccountsModal } from '../modals';
import { AccountTileSmall, AddAccountsModal, View } from '@widgets';
import { Button, Text } from '@shared/widgets';

interface AccountsProps {
  className?: string;
  user: UserResponse;
}

export const Accounts = (props: AccountsProps) => {
  const userAccountsStore = useUnit($userAccountsStore.store);

  const [showAddAccounts, setShowAddAccounts] = useState<boolean>(false);
  const [showRemoveAccounts, setShowRemoveAccounts] = useState<boolean>(false);

  useEffect(() => {
    $userAccountsStore.events.getUserAccounts({ AssignedUserId: props.user.id });
    return () => $userAccountsStore.events.clear();
  }, [props.user.id]);

  const { t } = useTranslation('user');

  return (
    <section className={cn('flex flex-col gap-6', props.className)}>
      <div className='flex flex-wrap items-center justify-between gap-4'>
        <Text weight='medium' size='2xl'>
          {t('accounts.title', { count: userAccountsStore.data.length })}
        </Text>
        {PermissionUtils.manageAccountsPermissions() && (
          <div className='flex items-center gap-3'>
            <Button color='outlined' onClick={() => setShowAddAccounts(true)}>
              {t('buttons.add', { ns: 'common' })}
            </Button>
            <Button color='outlined' disabled={!userAccountsStore.data.length} onClick={() => setShowRemoveAccounts(true)}>
              {t('buttons.remove', { ns: 'common' })}
            </Button>
          </div>
        )}
      </div>
      <View
        loading={userAccountsStore.loading}
        noData={!userAccountsStore.data.length}
        noDataText={t('noData.noRecords', { ns: 'common' })}
        error={userAccountsStore.error}
        onRefresh={() => $userAccountsStore.events.getUserAccounts({ AssignedUserId: props.user.id })}
      >
        <div className={cn('grid grid-cols-2 gap-6', 'max-[1400px]:grid-cols-1', props.user.locked && 'pointer-events-none select-none opacity-40')}>
          {userAccountsStore.data.map((account) => (
            <AccountTileSmall key={account.id} account={account} />
          ))}
        </div>
      </View>
      <AddAccountsModal
        isOpen={showAddAccounts}
        onClose={() => setShowAddAccounts(false)}
        user={props.user}
        accountsItems={userAccountsStore.data}
        onSubmit={(data) => $userAccountsStore.events.addAccounts(data)}
      />
      <RemoveAccountsModal show={showRemoveAccounts} userData={props.user} onClose={() => setShowRemoveAccounts(false)} />
    </section>
  );
};
